import { FRONTEND_FLAGS } from "../core/components/LaunchDarklyProvider";
import {
  Site,
  SiteFeatureFlag,
  SiteFeatureFlagValue,
  TestValues,
} from "../resources/modelTypes";
import { useCurrentSite } from "./models/SiteContext";
import { useMediaQueryState } from "@minted/minted-components";
import { useLDClient } from "launchdarkly-react-client-sdk";

/** Returns `featureFlags[flag]`, falling back to `siteDetails[flag]` during migration. */
export const getSiteFeatureFlag = (
  site: Site,
  flag: SiteFeatureFlagValue,
): TestValues | null | undefined =>
  site.featureFlags[flag] ??
  (site.siteDetails as Record<string, TestValues | null>)[flag];

/**
 * Hook to check if the OI Messages tab is enabled for this site.
 * The flag value is captured at site creation time and stored in feature_flags.
 */
export const useOIMessagesTabEnabled = (): boolean => {
  const site = useCurrentSite();
  return (
    getSiteFeatureFlag(site, SiteFeatureFlag.OiMessagesTab) ===
    TestValues.Experiment
  );
};

/**
 * Custom hooks to get experience flags.
 * They must be used inside a LaunchDarklyProvider.
 */
export const useNewMobileExperience = (): boolean => {
  const site = useCurrentSite();
  const mediumMediaQueryState = useMediaQueryState({
    mediaQuerySize: "medium",
  });
  return mediumMediaQueryState === "BELOW" && site.isOnlineInvitation;
};

export const useNewDesktopExperience = (): boolean => {
  const site = useCurrentSite();
  const mediumMediaQueryState = useMediaQueryState({
    mediaQuerySize: "medium",
  });

  if (!site.isOnlineInvitation) {
    return false;
  }

  if (mediumMediaQueryState === "BELOW") {
    return false;
  }

  return (
    getSiteFeatureFlag(site, SiteFeatureFlag.SectionEditorExperience) ===
    TestValues.Experiment
  );
};

export const useGuestHouseholdMembersFlag = (): boolean => {
  const site = useCurrentSite();
  return (
    getSiteFeatureFlag(site, SiteFeatureFlag.ConnectHouseholdMembers) ===
    TestValues.Experiment
  );
};

export const useStripeRegistries = (): boolean => {
  const site = useCurrentSite();
  return (
    getSiteFeatureFlag(site, SiteFeatureFlag.StripeRegistry) ===
    TestValues.Experiment
  );
};

export const useIsClickToEditEnabled = (): boolean => {
  const site = useCurrentSite();
  const ldClient = useLDClient();
  const smallMediaQueryState = useMediaQueryState({
    mediaQuerySize: "small",
  });

  if (site.isOnlineInvitation) {
    return true;
  } else if (smallMediaQueryState === "BELOW") {
    return false;
  } else {
    // WW click-to-edit is desktop only
    const lowerCutoffDate = new Date("2026-01-27T12:00:00");
    const upperCutoffDate = new Date("2026-03-05T12:00:00");
    const siteCreatedAt = new Date(site.createdAt);

    // Include only sites created between cutoff dates in experiment
    if (siteCreatedAt < lowerCutoffDate || siteCreatedAt > upperCutoffDate) {
      return false;
    }

    const flagValue = ldClient
      ? ldClient.variation(FRONTEND_FLAGS.weddingClickToEdit, false)
      : false;

    return flagValue;
  }
};

/**
 * Hook to check if the Suite Upsell experience is enabled.
 * The flag value is captured at site creation time and stored in site_details.
 */
export const useSuiteUpsellFlag = (): boolean => {
  const site = useCurrentSite();
  if (!site) return false;
  return (
    getSiteFeatureFlag(site, SiteFeatureFlag.MatchingSuitePage) ===
    TestValues.Experiment
  );
};

/**
 * Hook to check if automatic party-size parsing from contact names is enabled.
 */
export const useAutoParseContactNameFlag = (): boolean => {
  const site = useCurrentSite();
  const ldClient = useLDClient();
  if (site.isOnlineInvitation) return false;
  return ldClient
    ? ldClient.variation(FRONTEND_FLAGS.autoParseContactName, false)
    : false;
};
